import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { HeartIcon, PauseIcon, PlayIcon, RadioIcon, SkipBackIcon, SkipForwardIcon, Volume1Icon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { usePlayer } from '../contexts/PlayerContext';
import { useLibrary } from '../contexts/LibraryContext';
import { formatTime } from '../utils/format';
import { Visualizer, type VisualizerStyle } from './Visualizer';
import { SleepMenu } from './SleepMenu';
import { ThemeMenu } from './ThemeMenu';
import { Tooltip } from './Tooltip';
import { Waveform } from './Waveform';
interface PlayerBarProps {
  visualizerStyle: VisualizerStyle;
}
export function PlayerBar({
  visualizerStyle
}: PlayerBarProps) {
  const {
    currentTrack,
    isPlaying,
    isLoading,
    progress,
    duration,
    volume,
    muted,
    drift,
    isDrifting,
    toggle,
    next,
    previous,
    seek,
    setVolume,
    toggleMute,
    setDrift
  } = usePlayer();
  const {
    isFavorite,
    toggleFavorite
  } = useLibrary();
  const VolumeIcon = muted || volume === 0 ? VolumeXIcon : volume < 0.5 ? Volume1Icon : Volume2Icon;
  const pct = duration > 0 ? Math.min(100, progress / duration * 100) : 0;
  const scrubber = <div className="relative flex-1" data-id="element-66" data-mp="351rmw5uq1j23">
      <div className="h-1 w-full rounded-full bg-ink-line" data-id="element-67" data-mp="395h94onxbojy" />
      <motion.div className="pointer-events-none absolute left-0 top-0 h-1 rounded-full bg-gold" style={{
      width: `${pct}%`
    }} data-id="element-68" data-mp="392v34tgolh3u" />
      <input type="range" min={0} max={duration || 30} step={0.1} value={progress} onChange={e => seek(Number(e.target.value))} aria-label="Seek within preview" disabled={!currentTrack} className="absolute inset-0 h-1 w-full cursor-pointer" data-id="element-69" data-mp="2hf31ha6i78gq" />
    </div>;
  return <div className="relative flex-none border-t border-ink-line bg-ink-panel/95 px-4 py-3 backdrop-blur" data-id="element-70" data-mp="1tmmhuv74b8fb">
      <AnimatePresence data-id="element-71" data-mp="310ybvss984s7">
        {isDrifting && <motion.p initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 4
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute -top-9 left-1/2 -translate-x-1/2 rounded-full border border-iris-soft/40 bg-ink-panel px-3 py-1.5 text-xs text-iris-soft" data-id="element-72" data-mp="30zn83syt6x13">
            Night Drift is pulling the next records…
          </motion.p>}
      </AnimatePresence>

      <div className="flex items-center gap-4" data-id="element-73" data-mp="2d410y36g0362">
        {/* Now playing */}
        <div className="flex min-w-0 flex-1 items-center gap-3 md:w-72 md:flex-none lg:w-80" data-id="element-74" data-mp="2r3a3w15n10s3">
          <Link to="/now-playing" aria-label="Open now playing and lyrics" className="relative h-12 w-12 flex-none overflow-hidden rounded bg-ink-raised ring-1 ring-cream/5 transition-transform duration-150 ease-night hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-75" data-mp="2n1f3cyvj6rfd">
            <AnimatePresence mode="popLayout" data-id="element-76" data-mp="9t5rn0wmum49">
              {currentTrack?.artworkSmall && <motion.img key={currentTrack.id} src={currentTrack.artworkSmall} alt="" initial={{
              opacity: 0,
              scale: 0.96
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} className="absolute inset-0 h-full w-full object-cover" data-id="element-77" data-mp="ev4tg6cq5az3" />}
            </AnimatePresence>
          </Link>
          <div className="min-w-0" data-id="element-78" data-mp="iu3v9i35srmb">
            <p className="truncate text-sm font-medium text-cream" data-id="element-79" data-mp="it2lri1skd6p">
              <Link to="/now-playing" className="rounded transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-80" data-mp="nsxb3h5x4upn">
                {currentTrack?.title ?? 'Nothing queued'}
              </Link>
            </p>
            <p className="truncate text-xs text-muted" data-id="element-81" data-mp="3opiwf9p48vk2">
              {currentTrack ? currentTrack.artist : 'Pick a track to start the set'}
            </p>
          </div>
          {/* Live wave off the playing audio */}
          <div className="hidden h-8 w-20 flex-none lg:block" data-id="element-82" data-mp="39ucr6oxuh0rs">
            <Waveform bars={18} data-id="element-83" data-mp="39w5x220mvgyq" />
          </div>
          {currentTrack && <button type="button" onClick={() => toggleFavorite(currentTrack)} aria-pressed={isFavorite(currentTrack.id)} aria-label={isFavorite(currentTrack.id) ? 'Remove from saved' : 'Save track'} className="hidden rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-iris-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft sm:block" data-id="element-84" data-mp="bbe62k4hp9ks">
              <HeartIcon className={`h-4 w-4 ${isFavorite(currentTrack.id) ? 'fill-iris-soft text-iris-soft' : ''}`} aria-hidden="true" data-id="element-85" data-mp="pc8fn639xgl1" />
            </button>}
        </div>

        {/* Transport */}
        <div className="flex flex-none flex-col items-center gap-1.5 md:flex-1" data-id="element-86" data-mp="11osp8hm36xt1">
          <div className="flex items-center gap-2" data-id="element-87" data-mp="16k81x68cynnv">
            <button type="button" onClick={previous} aria-label="Previous track" className="rounded-full p-2 text-muted transition-colors duration-150 ease-night hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-88" data-mp="1blf6yxj498qu">
              <SkipBackIcon className="h-4 w-4" aria-hidden="true" data-id="element-89" data-mp="1pdbwquxbb6k2" />
            </button>
            <button type="button" onClick={toggle} aria-label={isPlaying ? 'Pause' : 'Play'} className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-ink-base transition-transform duration-150 ease-night hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-panel" data-id="element-90" data-mp="1ubn6l5nlxh2a">
              {isPlaying ? <PauseIcon className="h-5 w-5" aria-hidden="true" data-id="element-91" data-mp="1oayxs074ppbg" /> : <PlayIcon className="h-5 w-5 translate-x-[1px]" aria-hidden="true" data-id="element-92" data-mp="1jkiisbnntvmz" />}
            </button>
            <button type="button" onClick={next} aria-label="Next track" className="rounded-full p-2 text-muted transition-colors duration-150 ease-night hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-93" data-mp="1xbdudvpjmqu9">
              <SkipForwardIcon className="h-4 w-4" aria-hidden="true" data-id="element-94" data-mp="22aqi0io8o1xw" />
            </button>
          </div>
          <div className="hidden w-full max-w-xl items-center gap-2 md:flex" data-id="element-95" data-mp="cx3fiikxh0g0">
            <span className="w-9 text-right text-[11px] tabular-nums text-muted" data-id="element-96" data-mp="7xigy99o6u3t">{formatTime(progress)}</span>
            {scrubber}
            <span className="w-9 text-[11px] tabular-nums text-muted" data-id="element-97" data-mp="2jlnfc2u3ezjo">
              {isLoading && !progress ? '--:--' : formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Night Drift, sleep fade, theme, visualizer, volume */}
        <div className="flex flex-none items-center justify-end gap-2 md:w-[19rem] lg:w-[22rem]" data-id="element-98" data-mp="2etc9ykgl2ljq">
          <Tooltip align="right" content={<>
                <span className="block font-display text-base text-cream" data-id="element-100" data-mp="24vg0ry77i0o9">Night Drift</span>
                <span className="mt-1.5 block text-xs leading-relaxed text-muted" data-id="element-101" data-mp="1qvn4zogtou9f">
                  When the queue runs dry, Noctuzan reads the last record&apos;s genre and artist, pulls fresh cuts
                  from the live catalogue, and slides them in behind you — an endless late-night set.
                </span>
                <span className="mt-2 block text-xs leading-relaxed text-muted" data-id="element-102" data-mp="3q9ar7blx0dw1">
                  Pair it with the sleep fade beside it and the room dims itself to silence.
                </span>
                <span className="mt-2.5 block text-[11px] text-gold" data-id="element-103" data-mp="28zt0m0twrilo">
                  {drift ? 'On — drifting after the last track.' : 'Off — the queue stops when it ends.'}
                </span>
              </>} data-id="element-99" data-mp="20u3002iz0no7">
            {tooltipProps => <button type="button" {...tooltipProps} onClick={() => setDrift(!drift)} aria-pressed={drift} className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs transition-colors duration-150 ease-night focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft ${drift ? 'border-gold/70 bg-gold/10 text-gold' : 'border-ink-line text-muted hover:text-cream'}`} data-id="element-104" data-mp="1z8otfnah521e">
                <RadioIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-105" data-mp="3apevqvbs19pq" />
                <span className="hidden sm:inline" data-id="element-106" data-mp="3i97az6wns6cg">Night Drift</span>
              </button>}
          </Tooltip>
          <SleepMenu data-id="element-107" data-mp="3n7r35p1uied9" />
          <ThemeMenu data-id="element-108" data-mp="3n6zeyzakvix0" />
          <Visualizer style={visualizerStyle} className="hidden h-12 w-12 lg:block" data-id="element-109" data-mp="3n6fyzo4hn4lm" />
          <button type="button" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'} className="hidden rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft md:block" data-id="element-110" data-mp="3rziu40owym3x">
            <VolumeIcon className="h-4 w-4" aria-hidden="true" data-id="element-111" data-mp="398ixcdb6g803" />
          </button>
          <div className="relative hidden w-20 md:block" data-id="element-112" data-mp="39alz1ulozpkn">
            <div className="h-1 w-full rounded-full bg-ink-line" data-id="element-113" data-mp="3db5rikvbm50g" />
            <div className="pointer-events-none absolute left-0 top-0 h-1 rounded-full bg-iris-soft" style={{
            width: `${(muted ? 0 : volume) * 100}%`
          }} data-id="element-114" data-mp="oik316n21xfy" />
            <input type="range" min={0} max={1} step={0.01} value={muted ? 0 : volume} onChange={e => setVolume(Number(e.target.value))} aria-label="Volume" className="absolute inset-0 h-1 w-full cursor-pointer" data-id="element-115" data-mp="2hxz31kfoupea" />
          </div>
        </div>
      </div>

      {/* Mobile progress line */}
      <div className="mt-2.5 flex items-center gap-2 md:hidden" data-id="element-116" data-mp="1yzczlywp8m77">
        <span className="w-9 text-right text-[11px] tabular-nums text-muted" data-id="element-117" data-mp="1z17plrmtv4i5">{formatTime(progress)}</span>
        {scrubber}
        <span className="w-9 text-[11px] tabular-nums text-muted" data-id="element-118" data-mp="28whqgc1hp5uc">{formatTime(duration)}</span>
      </div>
    </div>;
}